import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { Users, Clock, Activity, Target, Award } from 'lucide-react';
import type { TeacherStudentsSummaryOut } from '../../../tutor/types/teacherTutorAnalytics';

interface TeacherTutorAggregateCardsProps {
  data: TeacherStudentsSummaryOut;
}

export const TeacherTutorAggregateCards: React.FC<TeacherTutorAggregateCardsProps> = ({ data }) => {
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const speakingLevelColors = {
    A0: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    A1: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    A2: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    B1: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    B2: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    C1: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    C2: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {/* Total Students */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Total Students
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {data.total_students || 0}
          </div>
          <p className="text-xs text-gray-500 mt-1">with tutor activity</p>
        </CardContent>
      </Card>

      {/* Total Tutor Minutes */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Total Minutes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {data.aggregate.total_tutor_minutes > 0 ? formatDuration(data.aggregate.total_tutor_minutes) : '0m'}
          </div>
          <p className="text-xs text-gray-500 mt-1">across all students</p>
        </CardContent>
      </Card>

      {/* Total Sessions */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Total Sessions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {data.aggregate.total_sessions || 0}
          </div>
          <p className="text-xs text-gray-500 mt-1">completed sessions</p>
        </CardContent>
      </Card>

      {/* Average Score */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
            <Target className="w-4 h-4" />
            Avg Score
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {data.aggregate.avg_overall_score !== null ? `${data.aggregate.avg_overall_score}%` : '—'}
          </div>
          <p className="text-xs text-gray-500 mt-1">overall performance</p>
        </CardContent>
      </Card>

      {/* Speaking Level Distribution */}
      <Card className="md:col-span-2 lg:col-span-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
            <Award className="w-4 h-4" />
            Speaking Level Distribution
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.aggregate.speaking_level_distribution || {}).map(([level, count]) => (
              <span
                key={level}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  speakingLevelColors[level as keyof typeof speakingLevelColors] ||
                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                {level}: {count}
              </span>
            ))}
            {Object.keys(data.aggregate.speaking_level_distribution || {}).length === 0 && (
              <span className="text-gray-500 text-sm">No speaking data yet</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};